import React, { useEffect, useRef, useState } from 'react';
import {
  AdjustmentsHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useDismissOnOutsideClick } from '../../hooks/useDismissOnOutsideClick';

// Shared building blocks of the Sessions and Events list pages. Every class name
// is `${prefix}-...`, so each page keeps its own stylesheet.

export const ListSearch = ({ prefix, placeholder, value, onChange }) => (
  <div className={`${prefix}-input ${prefix}-search`}>
    <MagnifyingGlassIcon />
    <input placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} />
  </div>
);

/** "Filters" button with an active-filter count and a popover that closes on an outside click. */
export const FilterPopover = ({ prefix, activeCount, resetLabel, onReset, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);
  useDismissOnOutsideClick(wrapRef, isOpen, () => setIsOpen(false));

  return (
    <div className={`${prefix}-filter-wrap`} ref={wrapRef}>
      <button
        type="button"
        className={`${prefix}-btn ${activeCount ? 'active' : ''}`}
        onClick={() => setIsOpen((open) => !open)}
      >
        <AdjustmentsHorizontalIcon />
        Filters
        {activeCount > 0 && <span className={`${prefix}-filter-count`}>{activeCount}</span>}
      </button>

      {isOpen && (
        <div className={`${prefix}-popover`}>
          <div className={`${prefix}-popover-head`}>
            <strong>Filters</strong>
            {activeCount > 0 && <button type="button" onClick={onReset}>{resetLabel}</button>}
          </div>
          {children}
        </div>
      )}
    </div>
  );
};

/** Loading, error or empty card shown in place of the list. */
export const ListPlaceholder = ({ prefix, Icon, title, detail, isLoading = false, isError = false }) => (
  <div className={`${prefix}-empty ${prefix}-card-shell${isError ? ` ${prefix}-error` : ''}`}>
    {isLoading ? <div className={`${prefix}-spinner`} /> : <Icon />}
    <div>
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  </div>
);

export const ListPagination = ({ prefix, currentPage, totalPages, total, rangeStart, rangeEnd, goToPage }) => {
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const handleSubmit = (event) => {
    event.preventDefault();
    goToPage(pageInput);
  };

  const isFirst = currentPage === 1;
  const isLast = currentPage === totalPages;

  return (
    <div className={`${prefix}-pagination`}>
      <span>{rangeStart}-{rangeEnd} of {total}</span>
      <div className={`${prefix}-pagination-controls`}>
        <button disabled={isFirst} onClick={() => goToPage(1)}>
          First
        </button>
        <button disabled={isFirst} onClick={() => goToPage(currentPage - 1)} aria-label="Previous page">
          <ChevronLeftIcon />
        </button>

        <form className={`${prefix}-page-jump`} onSubmit={handleSubmit}>
          <span>Page</span>
          <input
            type="number"
            min="1"
            max={totalPages}
            value={pageInput}
            onChange={(event) => setPageInput(event.target.value)}
            onBlur={() => setPageInput(String(currentPage))}
          />
          <span>of {totalPages}</span>
        </form>

        <button disabled={isLast} onClick={() => goToPage(currentPage + 1)} aria-label="Next page">
          <ChevronRightIcon />
        </button>
        <button disabled={isLast} onClick={() => goToPage(totalPages)}>
          Last
        </button>
      </div>
    </div>
  );
};
